import { Directive, OnDestroy, OnInit, Input, ElementRef, Renderer2, HostListener } from '@angular/core';
import { AccesilityService } from '../services/accesibility.service';
import { Subscription } from 'rxjs';

/**
 * Directive: appSignLanguageVideo
 *
 * Carga y reproduce un video de lengua de señas sobre el elemento host.
 * - Lazy load: el src del video se asigna solo cuando el usuario hace hover.
 * - El video se pre-carga en background usando IntersectionObserver para
 *   elementos cercanos al viewport, mejorando la experiencia sin afectar
 *   el rendimiento inicial.
 *
 * Uso:
 *   <span appSignLanguageVideo videoSrc="assets/signs/hola.mp4">Hola</span>
 */

@Directive({
    selector: '[appLenguajeSenasVideo]',
})
export class LenguajeSenasVideoDirective implements OnInit, OnDestroy {
    @Input() videoSrc: string = '';
    @Input() posicionVideo: 'top' | 'bottom' | 'left' | 'right' = 'top';
    @Input() textoAlternativo: string = '';
    private overlayElement: HTMLElement | null = null;
    private videoElement: HTMLVideoElement | null = null;
    private intersectionOberserver: IntersectionObserver | null = null;
    private precarga = false;
    private hideTimeout: any = null;
    private signLanguageSubscription?: Subscription;
    private centeringTransform = '';

    constructor(
        private el: ElementRef<HTMLElement>,
        private renderer: Renderer2,
        private a11y: AccesilityService
    ) {}
    ngOnInit(): void {
        this.setupHostElement();
        this.setupIntersectionObserver();
        this.signLanguageSubscription = this.a11y.signLanguage$.subscribe(() => {
            this.actualizarEstadoVisual();
        });
    }
    ngOnDestroy(): void {
        this.intersectionOberserver?.disconnect();
        this.signLanguageSubscription?.unsubscribe();
        clearTimeout(this.hideTimeout);
        this.destruirOverlay();
    }

    @HostListener('mouseenter')
    @HostListener('focusin')
    onEnter(): void {
        if (!this.a11y.isSignLanguageEnabled()) return;

        clearTimeout(this.hideTimeout);
        this.mostrarVideo();
    }

    @HostListener('mouseleave')
    @HostListener('focusout')
    onLeave(): void {
        // Pequeño delay para evitar parpadeo si el mouse pasa rozando
        this.hideTimeout = setTimeout(() => this.ocultarVideo(), 150);
    }

    private setupHostElement(): void {
        const host = this.el.nativeElement;
        this.renderer.setAttribute(host, 'tabindex', '0');
        this.renderer.setAttribute(host, 'aria-label', `${host.textContent?.trim()} — disponible en lengua de señas`);
        this.actualizarEstadoVisual();
        this.renderer.setStyle(host, 'position', 'relative');
        this.renderer.setStyle(host, 'display', 'inline-block');
    }

    private setupIntersectionObserver(): void {
        if (!('IntersectionObserver' in window)) return;

        this.intersectionOberserver = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !this.precarga) {
                    this.precargarVideo();
                    this.intersectionOberserver?.disconnect();
                }
            },
            { rootMargin: '200px' }
        );

        this.intersectionOberserver.observe(this.el.nativeElement);
    }

    /**
     * Crea un <video> oculto en memoria para pre-cachear el archivo.
     * No se agrega al DOM todavía.
     */
    private precargarVideo(): void {
        if (!this.videoSrc || this.precarga) return;
        this.precarga = true;
    }

    private mostrarVideo(): void {
        if (!this.videoSrc) return;
        if (!this.overlayElement) {
            this.crearOverlay();
        }

        if (this.videoElement && !this.videoElement.getAttribute('src')) {
            this.videoElement.src = this.videoSrc;
        }

        this.videoElement?.play().catch(() => {});

        this.renderer.setStyle(this.overlayElement, 'opacity', '1');
            this.renderer.setStyle(this.overlayElement, 'transform', `${this.centeringTransform} translateY(0) scale(1)`);
        this.renderer.setStyle(this.overlayElement, 'pointer-events', 'auto');
    }

    private ocultarVideo(): void {
        if (!this.overlayElement) return;

        this.renderer.setStyle(this.overlayElement, 'opacity', '0');
            this.renderer.setStyle(this.overlayElement, 'transform', `${this.centeringTransform} translateY(6px) scale(0.97)`)
        this.renderer.setStyle(this.overlayElement, 'pointer-events', 'none');
        this.videoElement?.pause();
    }

    private crearOverlay(): void {
        const overlay = this.renderer.createElement('div') as HTMLElement;
        const video = document.createElement('video');
        const label = this.renderer.createElement('span') as HTMLElement;

        // Video
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        video.setAttribute('aria-hidden', 'true');
        this.applyStyles(video, {
            width: '160px',
            height: '160px',
            objectFit: 'cover',
            borderRadius: '12px',
            display: 'block',
        });

        // Label accesible
        label.textContent = this.textoAlternativo;
        this.applyStyles(label, {
            display: 'block',
            textAlign: 'center',
            fontSize: '11px',
            color: 'rgb(185, 60, 55)',
            marginTop: '6px',
            fontFamily: 'sans-serif',
        });

        // Overlay container
        this.applyStyles(overlay, {
            position: 'absolute',
            zIndex: '9999',
            background: '#FFFCF5',
            borderRadius: '16px',
            padding: '10px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.07)',
            opacity: '0',
            transform: 'translateY(6px) scale(0.97)',
            transition: 'opacity 0.2s ease, transform 0.2s ease',
            pointerEvents: 'none',
            width: '180px',
        });

        this.positionOverlay(overlay);

        overlay.appendChild(video);
        overlay.appendChild(label);
        this.el.nativeElement.appendChild(overlay);

        this.overlayElement = overlay;
        this.videoElement = video;
    }

    private applyStyles(el: HTMLElement, styles: Partial<CSSStyleDeclaration>): void {
        Object.entries(styles).forEach(([prop, value]) => {
            (el.style as any)[prop] = value;
        });
    }

    private destruirOverlay(): void {
        this.overlayElement?.remove();
        this.overlayElement = null;
        this.videoElement = null;
    }

    private positionOverlay(overlay: HTMLElement): void {
        const gap = '8px';
        const positions: Record<string, { css: Partial<CSSStyleDeclaration>; center: string }> = {
            top: { css: { bottom: '100%', left: '50%', marginBottom: gap }, center: 'translateX(-50%)' },
            bottom: { css: { top: '100%', left: '50%', marginTop: gap }, center: 'translateX(-50%)' },
            left: { css: { right: '100%', top: '50%', marginRight: gap }, center: 'translateY(-50%)' },
            right: { css: { left: '100%', top: '50%', marginLeft: gap }, center: 'translateY(-50%)' },
        };

        const pos = positions[this.posicionVideo] || positions['top'];
        this.centeringTransform = pos.center;

        Object.entries(pos.css).forEach(([prop, value]) => {
            (overlay.style as any)[prop] = value;
        });
        overlay.style.transform = `${this.centeringTransform} translateY(6px)`;
    }

    private actualizarEstadoVisual(): void {
        const activo = this.a11y.isSignLanguageEnabled();
        this.renderer.setStyle(this.el.nativeElement, 'cursor', activo ? 'pointer' : 'default');
        if (!activo) {
            this.ocultarVideo();
        }
    }
}
